import { canvas, bloom, setShadows } from './scene.js';
import { initAudio, resumeAudio } from './audio.js';
import { switchWeapon, startReload, W } from './weapons.js';
import { equipGrenade } from './grenades.js';
import { connect, sendHello, setTransport, linkInfo, BTN, BTN_NADE_SHIFT, NADE_KIND } from './net.js';
import { enterLobby } from './lobby.js';
import { S, keys, touchState, isTouch, BASE_FOV } from './state.js';

// ============================================================
//  Ввод: десктоп (Pointer Lock) и тач (стик + кнопки).
//  Плюс стартовый экран: ник + вход в игру.
// ============================================================

const overlay = document.getElementById('overlay');
const nickInput = document.getElementById('nickInput');
const nickErr = document.getElementById('nickErr');

// Ник помним между заходами, чтобы не вводить каждый раз
nickInput.value = localStorage.getItem('fps_nick') || '';

// ---- Выбор транспорта (WebSocket / WebRTC) ----
//
// Транспорт выбирается ДО входа в игру и потом не меняется: после hello
// на сервере уже есть наше тело, привязанное к соединению (подробно —
// в net.js::setTransport). Поэтому селектор блокируется при старте.
const transportBox = document.getElementById('transport');
const PING_EL = { ws: document.getElementById('tPingWs'), rtc: document.getElementById('tPingRtc') };

// Выбор помним между заходами — иначе при отладке «а как оно на другом
// транспорте» пришлось бы переключать каждый раз заново.
const savedTransport = localStorage.getItem('fps_transport');
if (savedTransport) setTransport(savedTransport);
for (const r of transportBox.querySelectorAll('input[name=transport]')) {
  r.checked = r.value === S.transport;
  r.addEventListener('change', () => {
    if (setTransport(r.value)) localStorage.setItem('fps_transport', r.value);
  });
}

// ---- Выбор мирности ботов ----
//
// ⚠️ Настройка КОМНАТНАЯ, а не персональная: комната в проекте одна на всех,
// боты в ней тоже одни, и вошедший последним меняет сложность всем
// (подробности и обоснование — в server/src/npc.rs у `Difficulty`).
// Поэтому и блокируется она так же, как транспорт: после входа менять
// нечего — кадр HELLO уже ушёл.
const diffBox = document.getElementById('difficulty');
const savedDiff = parseInt(localStorage.getItem('fps_difficulty'), 10);
if (Number.isInteger(savedDiff) && savedDiff >= 0 && savedDiff <= 3) S.difficulty = savedDiff;
for (const r of diffBox.querySelectorAll('input[name=difficulty]')) {
  r.checked = +r.value === S.difficulty;
  r.addEventListener('change', () => {
    S.difficulty = +r.value;
    localStorage.setItem('fps_difficulty', r.value);
  });
}

// Подключаемся сразу при открытии страницы: пинг должен быть виден
// ещё на стартовом экране, а не после входа в игру. Поднимаются ОБА
// транспорта — сравнение пингов и есть смысл этого экрана.
connect();

const STATE_SHORT = { off: '—', connecting: '…', error: 'нет связи' };

/** Живые пинги обоих транспортов на стартовом экране. */
function refreshTransportUI() {
  for (const kind of ['ws', 'rtc']) {
    const { state, ping } = linkInfo(kind);
    PING_EL[kind].textContent = state === 'online' && ping !== null
      ? `${Math.round(ping)} мс`
      : (STATE_SHORT[state] ?? '—');
    PING_EL[kind].className = 't-ping ' + (state !== 'online' ? 'bad'
      : ping === null ? '' : ping < 60 ? 'good' : ping < 130 ? 'mid' : 'bad');
    transportBox.querySelector(`.t-opt[data-kind=${kind}]`)
      .classList.toggle('down', state === 'error');
  }
}
// Полсекунды — быстрее незачем: пинг и так сглажен EMA, а DOM трогать чаще
// смысла нет. Таймер вечный, но на стартовом экране это две строки текста.
setInterval(refreshTransportUI, 500);
refreshTransportUI();

/** Клиентская проверка ника — только для удобства. Сервер валидирует свою. */
function validNick() {
  const v = nickInput.value.trim().replace(/\s+/g, ' ');
  if (v.length < 2 || v.length > 16) return null;
  return v;
}

/**
 * Представиться серверу и уйти в ЛОББИ, а не в игру.
 *
 * ⚠️ Ключевое изменение протокола 20: `hello` больше не вводит в матч. До
 * матчмейкинга комната была одна, и «войти» означало «оказаться в общем
 * матче»; теперь комнат много, и выбор — комната, команда, слот — делается
 * на экране лобби (`lobby.js`, кадр `OP_PICK`).
 */
function startGame() {
  const nick = validNick();
  if (!nick) {
    nickErr.classList.add('on'); nickInput.classList.add('bad'); nickInput.focus();
    return;
  }
  // Транспорт, который не поднялся, в игру не пускаем. Иначе вход
  // «сработал бы», но клиент молча свалился бы в офлайн-песочницу — и это
  // выглядит как «сервер сломался», а не как «выбран нерабочий транспорт».
  if (linkInfo(S.transport).state !== 'online') {
    transportBox.classList.add('warn');
    setTimeout(() => transportBox.classList.remove('warn'), 1200);
    return;
  }
  nickErr.classList.remove('on'); nickInput.classList.remove('bad');
  S.nick = nick;
  localStorage.setItem('fps_nick', nick);
  // Транспорт после hello не меняется: тело в комнате будет привязано к этому
  // соединению (подробно — net.js::setTransport).
  transportBox.classList.add('locked');
  for (const r of transportBox.querySelectorAll('input')) r.disabled = true;
  // ⚠️ А вот сложность БОЛЬШЕ НЕ блокируется, и это не забывчивость: она
  // уезжает кадром `OP_PICK` в момент входа в комнату, то есть её можно
  // менять между матчами, не переподключаясь.
  sendHello();
  // Звук готовим здесь: это последний клик пользователя перед игрой, а
  // Web Audio без жеста не запускается.
  initAudio(); resumeAudio();
  enterLobby();
}

// Клик по самой панели (инпут, подсказки клавиш) не должен стартовать игру —
// иначе нельзя даже поставить курсор в поле ввода.
// ⚠️ Селекторы тоже исключены, а не только поле ника и подсказки: раньше
// клик по строке транспорта одновременно выбирал его И стартовал игру
// с захватом мыши. С появлением второго селектора (сложность) это стало
// откровенно мешать: выбрать Hard, не провалившись сразу в игру, было нельзя.
//
// ⚠️ И только на экране настроек: на экранах лобби, ожидания и итогов у кликов
// своё назначение (слоты, «в бой», «покинуть матч») — там общий обработчик
// перехватывал бы их и «стартовал игру» посреди меню.
overlay.addEventListener('click', (e) => {
  if (!e.target.closest('#setup')) return;
  if (e.target.closest('.nick') || e.target.closest('.keys')
    || e.target.closest('.transport') || e.target.closest('.diff')) return;
  startGame();
});
nickInput.addEventListener('keydown', (e) => {
  e.stopPropagation();                  // Digit1/KeyR не должны переключать оружие при вводе ника
  if (e.code === 'Enter') startGame();
});
nickInput.addEventListener('input', () => {
  nickErr.classList.remove('on'); nickInput.classList.remove('bad');
});

/**
 * Текущее состояние ввода битовой маской — ровно то, что уходит на сервер.
 *
 * Тумблеры приседа/лёжа разворачиваются здесь в «зажато/не зажато»: сервер
 * не должен знать про тумблеры вообще, иначе пришлось бы синхронизировать
 * ещё и их состояние, а рассинхрон дал бы «встал на сервере, лежит у себя».
 */
/**
 * Прыжок из приседа/лёжа = просто встать.
 *
 * Пока игрок сидит или лежит, сервер прыжок не даёт (`movement.rs`: `!crouch && !prone`),
 * и раньше пробел там просто ничего не делал. Теперь он снимает позу. Нажатие при этом
 * «съедается» (`jumpConsumed`): иначе через кадр, когда поза уже снята, зажатый пробел
 * тут же ушёл бы прыжком — встал и сразу подпрыгнул одним нажатием.
 * Возвращает true, если нажатие ушло на вставание.
 */
let jumpConsumed = false;

function standUpInsteadOfJump() {
  if (!S.crouchToggle && !S.proneToggle && !touchState.crouch) return false;
  S.crouchToggle = false; S.proneToggle = false; touchState.crouch = false;
  // тач-кнопки — тумблеры с подсветкой, их состояние надо погасить вместе с позой
  document.getElementById('btnCrouch')?.classList.remove('on');
  document.getElementById('btnProne')?.classList.remove('on');
  jumpConsumed = true;
  return true;
}

export function currentButtons() {
  const prone = S.proneToggle;
  const crouch = (S.crouchToggle || touchState.crouch) && !prone;
  let b = 0;
  if (isTouch) {
    // Стик аналоговый, а протокол пока цифровой — раскладываем по осям.
    // Компромисс: тач теряет плавность подхода. Аналоговый ввод добавим
    // в протокол, когда дойдём до нормального мобильного управления.
    if (touchState.moveY < -0.3) b |= BTN.W;
    if (touchState.moveY > 0.3) b |= BTN.S;
    if (touchState.moveX < -0.3) b |= BTN.A;
    if (touchState.moveX > 0.3) b |= BTN.D;
  } else {
    if (keys['KeyW']) b |= BTN.W;
    if (keys['KeyS']) b |= BTN.S;
    if (keys['KeyA']) b |= BTN.A;
    if (keys['KeyD']) b |= BTN.D;
    if (keys['ShiftLeft'] || keys['ShiftRight']) b |= BTN.SPRINT;
  }
  if (keys['Space'] && !jumpConsumed) b |= BTN.JUMP;
  if (crouch) b |= BTN.CROUCH;
  if (prone) b |= BTN.PRONE;

  // ---- Что у нас в руках ----
  //
  // Едет в той же маске (биты 8..11) и на движение не влияет ни здесь, ни на
  // сервере. Нужно ровно для одного: чтобы противник ВИДЕЛ, чем мы вооружены
  // и целимся ли мы. Оружие сюда не кладём — его сервер и так знает
  // авторитетно из OP_SWITCH, а вот граната в руке и ПКМ жили только
  // на клиенте и наружу не выходили никак.
  if (S.nadeMode) {
    b |= BTN.NADE | (NADE_KIND[S.nadeMode] << BTN_NADE_SHIFT);
  } else if (S.aiming) {
    // С гранатой в руке прицела нет (`weapons.js::updateWeaponView` прячет
    // ствол), поэтому и бит не ставим: иначе противник увидел бы позу,
    // которую принять нельзя. По той же причине здесь `S.aiming`, а не
    // `S.ads`: на бегу оружие прижато к корпусу, и сервер бит всё равно
    // погасит (`room.rs::held_byte`).
    b |= BTN.ADS;
  }
  return b;
}

// ⚠️ Обработчик `pointerlockchange` живёт теперь в `lobby.js`, и это не
// перенос ради переноса: оверлей стал многоэкранным (настройки → лобби →
// ожидание → бой → пауза → итоги), и «показать оверлей, когда мышь отпущена»
// перестало быть однозначным действием. Решает тот, кто знает фазу матча.

document.addEventListener('mousemove', (e) => {
  if (document.pointerLockElement !== canvas) return;
  // Чувствительность падает только в РЕАЛЬНОМ прицеле: на бегу ПКМ зажата,
  // а прицела нет — замедлять обзор было бы не за что.
  const sens = S.aiming ? (0.0022 * W().adsFov / BASE_FOV) : 0.0022;
  S.yaw -= e.movementX * sens; S.pitch -= e.movementY * sens;
  S.pitch = Math.max(-Math.PI / 2 + 0.01, Math.min(Math.PI / 2 - 0.01, S.pitch));
});

addEventListener('keydown', (e) => {
  keys[e.code] = true;
  if (e.code === 'Space' && !e.repeat) standUpInsteadOfJump();
  if (e.code === 'KeyR') startReload();
  if (e.code === 'Digit1') switchWeapon('pistol');
  if (e.code === 'Digit2') switchWeapon('rifle');
  if (e.code === 'Digit3') switchWeapon('sniper');
  if (e.code === 'KeyB') { S.bloomOn = !S.bloomOn; bloom.enabled = S.bloomOn; }
  // Тени — тумблер. ⚠️ Выбор помнится в localStorage: перещёлкивать его каждый
  // заход никто не станет, а на слабой машине это разница между 60 кадрами и 45.
  if (e.code === 'KeyN' && !e.repeat) {
    S.shadowsOn = !S.shadowsOn;
    setShadows(S.shadowsOn);
    try { localStorage.setItem('fps_shadows', S.shadowsOn ? '1' : '0'); } catch { /* приватный режим */ }
  }
  // тумблеры поз: !e.repeat — иначе авто-повтор клавиши переключал бы их десятки раз в секунду
  if (e.code === 'KeyZ' && !e.repeat) { S.proneToggle = !S.proneToggle; if (S.proneToggle) S.crouchToggle = false; }
  if ((e.code === 'ControlLeft' || e.code === 'ControlRight' || e.code === 'KeyC') && !e.repeat) {
    S.crouchToggle = !S.crouchToggle; if (S.crouchToggle) S.proneToggle = false;
  }
  // Отладка: координаты и поверхность под ногами. F3 — как в Minecraft, чтобы
  // не объяснять клавишу отдельно. preventDefault обязателен: в Firefox F3 —
  // «найти следующее», и без него поверх игры открывается панель поиска.
  if (e.code === 'F3' && !e.repeat) { e.preventDefault(); S.debugPos = !S.debugPos; }
  // гранаты: сначала берём в руку (показываем траекторию), замах/бросок — ЛКМ
  if (e.code === 'Digit4') equipGrenade('frag');
  if (e.code === 'Digit5') equipGrenade('smoke');
  if (e.code === 'Digit6') equipGrenade('flash');
  if (e.code === 'Digit7') equipGrenade('molotov');
});
addEventListener('keyup', (e) => {
  keys[e.code] = false;
  if (e.code === 'Space') jumpConsumed = false;
});

canvas.addEventListener('mousedown', (e) => {
  if (document.pointerLockElement !== canvas) return;
  if (e.button === 0) { S.firing = true; S.semiQueued = true; }
  if (e.button === 2) S.ads = true;
});
addEventListener('mouseup', (e) => {
  if (e.button === 0) S.firing = false;
  if (e.button === 2) S.ads = false;
});
canvas.addEventListener('contextmenu', e => e.preventDefault());

// ============================================================
//  Тач-управление (телефон).
// ============================================================
if (isTouch) {
  const stick = document.getElementById('stick'), knob = stick.querySelector('.knob');
  let moveId = null, lookId = null, mOx = 0, mOy = 0, lLx = 0, lLy = 0;
  const halfW = () => innerWidth * 0.45;

  document.getElementById('touch').addEventListener('touchstart', handleStart, { passive: false });
  function handleStart(e) {
    for (const t of e.changedTouches) {
      if (t.target.classList.contains('btn')) continue;  // кнопки сами по себе
      if (t.clientX < halfW() && moveId === null) {      // левая зона — стик
        moveId = t.identifier; mOx = t.clientX; mOy = t.clientY;
        stick.style.display = 'block';
        stick.style.left = (t.clientX - 60) + 'px'; stick.style.top = (t.clientY - 60) + 'px';
        knob.style.transform = 'translate(0,0)';
      } else if (lookId === null) {                       // правая зона — обзор
        lookId = t.identifier; lLx = t.clientX; lLy = t.clientY;
      }
    }
    e.preventDefault();
  }

  addEventListener('touchmove', (e) => {
    for (const t of e.changedTouches) {
      if (t.identifier === moveId) {
        let dx = t.clientX - mOx, dy = t.clientY - mOy;
        const len = Math.hypot(dx, dy), max = 50;
        if (len > max) { dx = dx / len * max; dy = dy / len * max; }
        knob.style.transform = `translate(${dx}px,${dy}px)`;
        touchState.moveX = dx / max; touchState.moveY = dy / max;
      } else if (t.identifier === lookId) {
        S.yaw -= (t.clientX - lLx) * 0.005; S.pitch -= (t.clientY - lLy) * 0.005;
        S.pitch = Math.max(-Math.PI / 2 + 0.01, Math.min(Math.PI / 2 - 0.01, S.pitch));
        lLx = t.clientX; lLy = t.clientY;
      }
    }
  }, { passive: false });

  addEventListener('touchend', (e) => {
    for (const t of e.changedTouches) {
      if (t.identifier === moveId) {
        moveId = null; touchState.moveX = touchState.moveY = 0; stick.style.display = 'none';
      }
      if (t.identifier === lookId) lookId = null;
    }
  });

  const btn = (id, down, up) => {
    const el = document.getElementById(id);
    el.addEventListener('touchstart', e => { e.preventDefault(); down(); }, { passive: false });
    if (up) el.addEventListener('touchend', e => { e.preventDefault(); up(); }, { passive: false });
  };
  btn('btnFire', () => { S.firing = true; S.semiQueued = true; }, () => S.firing = false);
  btn('btnJump', () => { keys['Space'] = true; standUpInsteadOfJump(); },
    () => { keys['Space'] = false; jumpConsumed = false; });
  btn('btnReload', startReload);
  btn('btnAds', () => S.ads = !S.ads);
  btn('btnWeap', () => {
    const o = ['pistol', 'rifle', 'sniper']; switchWeapon(o[(o.indexOf(S.curKey) + 1) % 3]);
  });
  document.getElementById('btnCrouch').addEventListener('touchstart', e => {
    e.preventDefault();
    touchState.crouch = !touchState.crouch; e.target.classList.toggle('on', touchState.crouch);
  }, { passive: false });
  document.getElementById('btnProne').addEventListener('touchstart', e => {
    e.preventDefault();
    S.proneToggle = !S.proneToggle; e.target.classList.toggle('on', S.proneToggle);
  }, { passive: false });

  const NADE_ORDER = ['frag', 'smoke', 'flash', 'molotov'];
  const NADE_ICON = { frag: '🧨', smoke: '💨', flash: '⚡', molotov: '🔥' };
  let touchNade = 0;
  // 1-й тап — берём гранату в руку; затем зажатие копит замах, отпускание бросает
  btn('btnNade',
    () => { if (!S.nadeMode) equipGrenade(NADE_ORDER[touchNade]); else S.firing = true; },
    () => { if (S.nadeMode) S.firing = false; });
  document.getElementById('btnNadeType').addEventListener('touchstart', e => {
    e.preventDefault();
    touchNade = (touchNade + 1) % 4; e.target.textContent = NADE_ICON[NADE_ORDER[touchNade]];
    // граната уже в руке — сразу меняем её (и этим отменяем начатый замах),
    // иначе на тач-экране сменить тип во время натягивания было нельзя вовсе
    if (S.nadeMode) equipGrenade(NADE_ORDER[touchNade]);
  }, { passive: false });
}
